
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Edit3, Save, X, Plus } from "lucide-react";

interface ExtendedUserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  phone?: string;
  linkedin_url?: string;
  location_city?: string;
  location_state?: string;
  location_country?: string;
  timezone?: string;
  bio?: string;
  years_experience?: number;
  specializations?: string[];
  work_hours_start?: string;
  work_hours_end?: string;
  preferred_communication?: string;
}

interface ProfessionalInfoSectionProps {
  userProfile: ExtendedUserProfile;
  onUpdate: (updates: Partial<ExtendedUserProfile>) => Promise<void>;
}

export const ProfessionalInfoSection: React.FC<ProfessionalInfoSectionProps> = ({
  userProfile,
  onUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    years_experience: userProfile.years_experience || 0,
    specializations: userProfile.specializations || [],
    linkedin_url: userProfile.linkedin_url || "",
  });
  const [newSpecialization, setNewSpecialization] = useState("");

  useEffect(() => {
    setFormData({
      years_experience: userProfile.years_experience || 0,
      specializations: userProfile.specializations || [],
      linkedin_url: userProfile.linkedin_url || "",
    });
  }, [userProfile]);

  const handleSave = async () => {
    const updates: Partial<ExtendedUserProfile> = {};
    
    if (formData.years_experience !== (userProfile.years_experience || 0)) {
      updates.years_experience = formData.years_experience;
    }
    if (JSON.stringify(formData.specializations) !== JSON.stringify(userProfile.specializations || [])) {
      updates.specializations = formData.specializations;
    }
    if (formData.linkedin_url !== (userProfile.linkedin_url || "")) {
      updates.linkedin_url = formData.linkedin_url || undefined;
    }

    if (Object.keys(updates).length > 0) {
      await onUpdate(updates);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      years_experience: userProfile.years_experience || 0,
      specializations: userProfile.specializations || [],
      linkedin_url: userProfile.linkedin_url || "",
    });
    setIsEditing(false);
  };

  const addSpecialization = () => {
    if (newSpecialization.trim() && !formData.specializations.includes(newSpecialization.trim())) {
      setFormData({
        ...formData,
        specializations: [...formData.specializations, newSpecialization.trim()]
      });
      setNewSpecialization("");
    }
  };

  const removeSpecialization = (index: number) => {
    setFormData({
      ...formData,
      specializations: formData.specializations.filter((_, i) => i !== index)
    });
  };

  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="yearsExperience">Years of Experience: {formData.years_experience}</Label>
          <Slider
            id="yearsExperience"
            min={0}
            max={50}
            step={1}
            value={[formData.years_experience]}
            onValueChange={(value) => setFormData({ ...formData, years_experience: value[0] })}
            className="w-full"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
          <Input 
            id="linkedinUrl" 
            value={formData.linkedin_url} 
            onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
            placeholder="https://linkedin.com/in/yourprofile"
            type="url"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Specializations</Label>
          <div className="flex gap-2">
            <Input 
              value={newSpecialization} 
              onChange={(e) => setNewSpecialization(e.target.value)}
              placeholder="Add a specialization"
              onKeyPress={(e) => e.key === 'Enter' && addSpecialization()}
            />
            <Button type="button" variant="outline" size="icon" onClick={addSpecialization}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.specializations.map((spec, index) => (
              <Badge 
                key={index} 
                variant="secondary" 
                className="cursor-pointer"
                onClick={() => removeSpecialization(index)}
              >
                {spec} ×
              </Badge>
            ))}
          </div>
        </div>
        
        <div className="flex gap-2 pt-2">
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Professional Information</h3>
        <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
          <Edit3 className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>
      
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Label className="block text-muted-foreground text-sm mb-1">Years of Experience</Label>
          <p className="text-sm">{userProfile.years_experience || "Not set"}</p>
        </div>
        <div>
          <Label className="block text-muted-foreground text-sm mb-1">LinkedIn Profile</Label>
          {userProfile.linkedin_url ? (
            <a 
              href={userProfile.linkedin_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline"
            >
              View Profile
            </a>
          ) : (
            <p className="text-sm text-muted-foreground">Not set</p>
          )}
        </div>
      </div>
      
      <div>
        <Label className="block text-muted-foreground text-sm mb-2">Specializations</Label>
        {userProfile.specializations && userProfile.specializations.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {userProfile.specializations.map((spec, index) => (
              <Badge key={index} variant="secondary">
                {spec}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No specializations added</p>
        )}
      </div>
    </div>
  );
};
